import { useState, useEffect, useRef } from 'react';
import { getDetailedAnalytics } from '../../utils/superAdminApi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt, FaRupeeSign, FaShoppingCart, FaStar, FaUsers } from 'react-icons/fa';
import {
  ResponsiveContainer,
  LineChart,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Line,
  Bar,
} from 'recharts';

const kpiCardData = (data) => [
  { title: 'Total Revenue', value: `${(data?.totalRevenue || 0).toLocaleString()}`, icon: <FaRupeeSign /> },
  { title: 'Total Orders', value: data?.totalOrders || 0, icon: <FaShoppingCart /> },
  { title: 'New Restaurants', value: data?.newRestaurants || 0, icon: <FaUsers /> },
  { title: 'Avg. Order Value', value: `${(data?.averageOrderValue || 0).toFixed(2)}`, icon: <FaStar /> },
];

export default function SuperAdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;
  const [cooldown, setCooldown] = useState(false);
  const cooldownTimeout = useRef(null);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (startDate) params.startDate = startDate.toISOString();
      if (endDate) params.endDate = endDate.toISOString();
      const res = await getDetailedAnalytics(params);
      setData(res.analytics);
    } catch (err) {
      if (err?.message?.includes('Quota exceeded') || err?.details === 'Quota exceeded.') {
        setError('Quota exceeded. Please wait a minute before trying again.');
        setCooldown(true);
        if (cooldownTimeout.current) clearTimeout(cooldownTimeout.current);
        cooldownTimeout.current = setTimeout(() => setCooldown(false), 60000); // 1 minute cooldown
      } else {
        setError('Failed to load analytics. Please try again later.');
      }
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const s = new Date();
    s.setDate(s.getDate() - 30);
    setDateRange([s, new Date()]);
  }, []);
  
  useEffect(() => {
    if (startDate && endDate && !cooldown) {
      fetchAnalytics();
    }
    // Clean up cooldown timer on unmount
    return () => {
      if (cooldownTimeout.current) clearTimeout(cooldownTimeout.current);
    };
  }, [startDate, endDate, cooldown]);

  const kpis = kpiCardData(data?.kpis);

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-white">Platform Analytics</h1>
        <div className="relative">
          <DatePicker
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => setDateRange(update)}
            isClearable={true}
            className="bg-gray-700 border border-gray-600 rounded-lg py-2 pl-10 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            placeholderText="Select date range"
            disabled={cooldown}
          />
          <FaCalendarAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {loading ? <div className="text-center py-8 text-gray-400">Loading analytics...</div> :
       error ? <div className="text-center py-8 text-red-400">{error}</div> :
      !data ? <div className="text-center py-8 text-gray-400">No data available for this period.</div> :
      (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {kpis.map(kpi => (
              <div key={kpi.title} className="bg-gray-800 p-6 rounded-xl border border-gray-700">
                <div className="flex items-center gap-4">
                    <div className="text-2xl text-purple-400">{kpi.icon}</div>
                    <div>
                        <p className="text-gray-400 text-sm">{kpi.title}</p>
                        <p className="text-white text-2xl font-bold">{kpi.value}</p>
                    </div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-semibold text-white mb-4">Revenue & Orders Over Time</h3>
               <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data.timeSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                  <XAxis dataKey="date" stroke="#A0AEC0" tickFormatter={(d) => new Date(d).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})} />
                  <YAxis yAxisId="left" stroke="#8B5CF6" />
                  <YAxis yAxisId="right" orientation="right" stroke="#34D399" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="revenue" stroke="#8B5CF6" name="Revenue" />
                  <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#34D399" name="Orders" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
               <h3 className="text-xl font-semibold text-white mb-4">Top Restaurants by Revenue</h3>
               <ResponsiveContainer width="100%" height={300}>
                <BarChart data={data.topRestaurantsByRevenue} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#4A5568" />
                    <XAxis type="number" stroke="#A0AEC0" tickFormatter={(val) => `₹${val/1000}k`} />
                    <YAxis type="category" dataKey="name" stroke="#A0AEC0" width={120} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="revenue" fill="#8B5CF6" name="Total Revenue" />
                </BarChart>
               </ResponsiveContainer>
            </div>
          </div>
        </>
      )}
    </div>
  );
} 